'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const GA_ID = 'G-PSVC94D4NW';
const CONSENT_KEY = 'cookie_consent';

function loadGA(): void {
    if (document.getElementById('ga-script')) return;
    const s = document.createElement('script');
    s.id = 'ga-script';
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]): void { (window as any).dataLayer.push(args); }
    gtag('js', new Date());
    gtag('config', GA_ID);
}

function removeGA(): void {
    // Delete GA cookies
    document.cookie = '_ga=; Max-Age=0; path=/; domain=.' + location.hostname;
    document.cookie = `_ga_${GA_ID.replace('G-', '')}=; Max-Age=0; path=/; domain=.${location.hostname}`;
    // Remove script
    const s = document.getElementById('ga-script');
    if (s) s.remove();
}

export default function CookieBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (stored === 'accepted') {
            loadGA();
        } else if (stored === 'rejected') {
            removeGA();
        } else {
            // No consent yet — show banner, don't load GA
            setVisible(true);
        }
    }, []);

    const accept = useCallback(() => {
        localStorage.setItem(CONSENT_KEY, 'accepted');
        loadGA();
        setVisible(false);
    }, []);

    const reject = useCallback(() => {
        localStorage.setItem(CONSENT_KEY, 'rejected');
        removeGA();
        setVisible(false);
    }, []);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ duration: 0.4, ease: EASE_OUT }}
                    className="fixed bottom-4 left-4 right-4 z-[60] flex justify-center pointer-events-none"
                >
                    <div className="pointer-events-auto w-full max-w-2xl glass-card rounded-2xl p-5 shadow-[0_12px_40px_rgba(15,23,42,0.15)] border border-white/60">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                                <Cookie className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-900 mb-1">Usamos cookies</p>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    Utilizamos cookies analíticas (Google Analytics) para mejorar tu experiencia. No se activan hasta que aceptes.{' '}
                                    <Link href="/cookies" className="text-blue-600 hover:underline">Ver política de cookies</Link>
                                </p>
                                <div className="flex items-center gap-3 mt-3">
                                    <button
                                        onClick={accept}
                                        className="px-5 py-2 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors active:scale-95"
                                    >
                                        Aceptar
                                    </button>
                                    <button
                                        onClick={reject}
                                        className="px-5 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200 hover:bg-slate-200 transition-colors active:scale-95"
                                    >
                                        Rechazar
                                    </button>
                                </div>
                            </div>
                            <button onClick={reject} aria-label="Cerrar aviso de cookies" className="p-1 text-slate-400 hover:text-slate-600 transition-colors shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// Export utility to re-show banner (for footer link)
export function resetCookieConsent() {
    localStorage.removeItem(CONSENT_KEY);
    window.location.reload();
}
